import { useCallback, useEffect, useRef, useState } from 'react';
import { POLL_MS, RemoteVersion } from '../components/appUpdate/updateModel';

/**
 * Reads `/version.json` on window focus, when the tab becomes visible, and every
 * 30 minutes. A hidden tab never checks. Failures stay silent: a local start or
 * a network blip must not look like an update.
 *
 * Returns the latest remote record and the time this hook first saw it change,
 * which `deriveUrgency` and the grace period are computed from in `UpdateProvider`.
 */
export function useAppVersionCheck(): { remote: RemoteVersion | null; check: () => Promise<void> } {
  const [remote, setRemote] = useState<RemoteVersion | null>(null);
  const inFlight = useRef(false);

  const check = useCallback(async () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) return;
      const data = (await response.json()) as RemoteVersion;
      if (data && typeof data === 'object') setRemote(data);
    } catch {
      // Silent by design.
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    void check();
    const id = window.setInterval(() => void check(), POLL_MS);
    const onVisible = () => {
      if (!document.hidden) void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [check]);

  return { remote, check };
}
